import { BaseWidget } from "./BaseWidget";
import { SelectControl } from "../controls/SelectControl";
import { IconButtonGroupControl } from "../controls/IconButtonGroupControl";
import { ColorControl } from "../controls/ColorControl";
import { BooleanControl } from "../controls/BooleanControl";
import { NestedItemControl } from "../controls/NestedItemControl";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import useWidgetPersona from "./_shared/useWidgetPersona";
import usePrintDisplayMode from "./_shared/usePrintDisplayMode";
import BiotechIcon from '@mui/icons-material/Biotech';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { useFirestoreGeneral } from "../../../../firebase/useFirestore";
import { client } from "../../../../hooks/Client";
import { useCallback, useContext } from "react";
import { fetchTemplateData } from "../utils/fetchTemplateData";
import { TemplateDataContext } from "../context/TemplateDataContext";
import { EditorContext } from "../context/EditorContext";
import ResearchVisualization from "../../../dataVisualizations/ResearchVisualization";
import WidgetSourcePlaceholder from "../components/WidgetSourcePlaceholder";
import LoadingSpinner from "../../../common/LoadingSpinner";
import notDeleted from "../../../../helpers/notDeleted";
import isQuestionField from "../../../../helpers/questionnaire/isQuestionField";
import isStandardQuestion from "../../../../helpers/questionnaire/isStandardQuestion";
import questionHeading from "../../../../helpers/questionnaire/questionHeading";
import { definitionCollection } from '../../../../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// ResearchWidget — picks one research and renders its questions via the
// shared `ResearchVisualization`. Sidebar controls:
//   - `researchId`   Research picker
//   - `displayMode`  navigator (default) / stacked (always stacked on the printvel)
//   - `showQuestion` question text above each chart (default on)
//   - `byNestedId`   collapsable accordion per question with visibility,
//                    graph-type + colour overrides (keyed by stable question ID)

/**
 * The `OutputEffects` ID a standard (MSI) question measures.
 *
 * @param {{EffectId?: string|string[]}} field
 * @returns {string}
 */
const effectIdOf = (field) => (Array.isArray(field.EffectId) ? field.EffectId[0] : field.EffectId) || '';

/**
 * Effect names for the standard questions among `fields`, read live.
 * Firestore caps `in` at 30 values, hence the chunks.
 *
 * @param {Array<object>} fields - QuestionnaireFields documents
 * @returns {Promise<Object<string, string>>} Effect name per OutputEffects ID
 */
const fetchEffectTitles = async (fields) => {
    const ids = [...new Set(fields.filter(isStandardQuestion).map(effectIdOf).filter(Boolean))];
    const titles = {};
    for (let i = 0; i < ids.length; i += 30) {
        const snap = await definitionCollection('OutputEffects')
            .where('CompagnyID', '==', client || '')
            .where('ID', 'in', ids.slice(i, i + 30))
            .get();
        snap.docs.map(d => d.data()).filter(notDeleted).forEach(e => { titles[e.ID] = e.Effect || ''; });
    }
    return titles;
};

/**
 * One 'Per vraag' entry. The label is the same name the reader sees above the
 * chart (`questionHeading`): for a standard question that is the effect, since
 * its question text is identical for every effect. Without a found effect the
 * question text stays — a list entry needs some label, unlike a heading.
 *
 * @param {object} field - QuestionnaireFields document
 * @param {Object<string, string>} effectTitles - Effect name per OutputEffects ID
 * @returns {{id: string, label: string}}
 */
const toNestedItem = (field, effectTitles) => ({
    id: field.ID,
    label: questionHeading(field, effectTitles[effectIdOf(field)] || '')
        || field.Question || field.Title || field.Type || 'Vraag',
});

/**
 * Resolve the questions (QuestionnaireFields) for a research's questionnaire.
 *
 * @param {string} researchId
 * @returns {Promise<Array<{id: string, label: string}>>}
 */
const resolveQuestions = async (researchId) => {
    if (!researchId) return [];
    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';

    if (isTemplate) {
        try {
            const blob = await fetchTemplateData();
            const research = (blob?.data?.research || []).find(r => r.ID === researchId);
            if (!research?.QuestionnaireID) return [];
            const all = blob?.data?.effects?.indicators || [];
            const effectTitles = Object.fromEntries(
                (blob?.data?.effects?.effects || []).map(e => [e.ID, e.Effect || ''])
            );
            return all
                .filter(i => i.QuestionnaireID === research.QuestionnaireID && isQuestionField(i))
                .slice()
                .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
                .map(i => toNestedItem(i, effectTitles));
        } catch (err) {
            console.error('ResearchWidget: failed to fetch template questions', err);
            return [];
        }
    }

    const researchSnap = await definitionCollection('Research')
        .where('CompagnyID', '==', client || '')
        .where('ID', '==', researchId)
        .limit(1)
        .get();
    const research = researchSnap.docs[0]?.data();
    if (!research?.QuestionnaireID) return [];

    const fieldsSnap = await definitionCollection('QuestionnaireFields')
        .where('CompagnyID', '==', client || '')
        .where('QuestionnaireID', '==', research.QuestionnaireID)
        .orderBy('Position', 'asc')
        .get();
    const fields = fieldsSnap.docs.map(d => d.data()).filter(notDeleted).filter(isQuestionField);
    const effectTitles = await fetchEffectTitles(fields);
    return fields.map(i => toNestedItem(i, effectTitles));
};

/**
 * @extends {BaseWidget}
 */
export class ResearchWidget extends BaseWidget {
    getName() { return 'Onderzoek'; }
    getCategory() { return 'research'; }
    getIcon() { return <BiotechIcon />; }

    getControls() {
        return {
            researchId: new SelectControl({
                label: 'Onderzoek',
                description: 'Kies een onderzoek',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const json = await fetchTemplateData();
                        values = json?.data?.research || [];
                    } else {
                        const researches = await definitionCollection('Research')
                            .where('CompagnyID', '==', client || '')
                            .orderBy('Position', 'asc')
                            .get();
                        values = researches.docs.map(doc => doc.data()).filter(r => !r.Deleted);
                    }

                    return {
                        choices: values.map(o => ({ value: o.ID, label: o.Title })),
                        allowSearch: true,
                    };
                },
            }),
            period: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de data van deze widget toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            displayMode: new IconButtonGroupControl({
                label: 'Weergave',
                description: 'Wissel tussen carousel (één vraag per keer) en gestapeld',
                defaultValue: 'navigator',
                options: {
                    buttons: [
                        { value: 'navigator', icon: <ViewCarouselIcon />, tooltip: 'Carousel' },
                        { value: 'stacked', icon: <ViewStreamIcon />, tooltip: 'Gestapeld' },
                    ],
                },
            }),
            // Standaard aan, anders dan bij de widget 'Vraag': een onderzoek
            // toont veel vragen, en boven elke vraag een eigen kop zetten kan
            // de maker niet. Zonder deze kop ziet de lezer alleen '3 / 12'.
            showQuestion: new BooleanControl({
                label: 'Vraag tonen',
                description: 'Zet de vraagtekst boven elke grafiek, zodat lezers zien over welke vraag het gaat.',
                defaultValue: true,
            }),
            graphColor: new ColorControl({
                label: 'Label- en legendakleur',
                description: 'Kleur van de labels en de legenda.',
                defaultValue: '',
            }),
            byNestedId: new NestedItemControl({
                label: 'Per vraag',
                description: 'Kies per vraag of hij in deze rapportage staat, en met welk grafiektype en welke kleur.',
                parentSettingKey: 'researchId',
                noParentText: 'Kies eerst een onderzoek.',
                emptyText: 'Geen vragen gevonden voor dit onderzoek.',
                nestedItemsResolver: (researchId) => resolveQuestions(researchId),
                children: {
                    // Per widget, dus per rapportage. Het oogje in de
                    // indicatorweergave (`LiveReportHidden`) zit op de vraag
                    // zelf en geldt voor élk onderzoek dat de vragenlijst
                    // deelt; deze widget leest dat bewust niet (inbox #242).
                    visible: new BooleanControl({
                        label: 'Tonen',
                        description: 'Zet uit om deze vraag alleen in deze rapportage weg te laten.',
                        defaultValue: true,
                    }),
                    graphType: new SelectControl({
                        label: 'Grafiektype',
                        defaultValue: '',
                        options: {
                            choices: [
                                { value: '', label: 'Standaard' },
                                { value: 'bar', label: 'Staafdiagram' },
                                { value: 'line', label: 'Lijndiagram' },
                            ],
                        },
                    }),
                    color: new ColorControl({
                        label: 'Kleur',
                        defaultValue: '',
                    }),
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} widget={this} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.researchId
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {Object<string, Object<string, any>>} [props.byNestedId]
 * @param {boolean} [props.showQuestion] - Toon de vraagtekst boven elke grafiek
 * @param {string} [props.graphColor] - Label- en legendakleur
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Widget period setting (PeriodOverrideControl)
 * @param {ResearchWidget} props.widget
 */
const Renderer = ({ researchId, displayMode = 'navigator', byNestedId, showQuestion = true, graphColor, period, widget }) => {
    const effectiveDisplayMode = usePrintDisplayMode(displayMode);
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);
    const { range: periodRange, startDate, endDate } = useWidgetPeriod(period);
    const editorCtx = useContext(EditorContext);
    const handleCurrentItemChange = useCallback((item) => {
        editorCtx?.setActiveNestedItemId?.(widget.id, item?.ID || null);
    }, [editorCtx, widget?.id]);

    const liveResearches = useFirestoreGeneral('Research', 'ID', isTemplate ? '' : (researchId || ''));
    const research = isTemplate
        ? (templateData?.data?.research || []).find(r => r.ID === researchId)
        : (Array.isArray(liveResearches) && liveResearches[0]);

    const personaId = useWidgetPersona();

    if (!research) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* Still resolving (hook hasn't answered / template blob loading) →
           render nothing; resolved-but-missing (orphan after a template copy)
           or nothing selected → editor-only select CTA. */
        const stillResolving = isTemplate
            ? !templateData
            : (researchId && !Array.isArray(liveResearches));
        if (stillResolving) return <LoadingSpinner />;
        return <WidgetSourcePlaceholder ctaLabel="Selecteer onderzoek" />;
    }

    return (
        <ResearchVisualization
            research={research}
            displayMode={effectiveDisplayMode}
            showFieldQuestion={showQuestion !== false}
            nestedSettings={byNestedId || {}}
            graphColor={graphColor}
            selectedPersonaId={personaId}
            startDate={startDate}
            endDate={endDate}
            periodRange={periodRange}
            templateData={isTemplate ? templateData : null}
            onCurrentItemChange={handleCurrentItemChange}
        />
    );
};
